import * as yup from 'yup';

const DATE_OF_BIRTH_PATTERN = /^\d{2}-\d{2}-\d{4}$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Converts the dateOfBirth field's "dd-mm-yyyy" value to the "yyyy-mm-dd" format the API expects.
export function formatDateOfBirth(dateOfBirth: string) {
  const [day, month, year] = dateOfBirth.split('-');
  return `${year}-${month}-${day}`;
}

// Parses a "yyyy-mm-dd" string to a UTC-midnight Date, or null when it isn't a real calendar date.
// `new Date` is too permissive on its own: it silently rolls overflow over ("2000-02-30" becomes
// 1 March) and returns an Invalid Date for nonsense months ("2000-13-45"), whose toISOString()
// throws a RangeError. The round-trip below rejects both.
function parseIsoDate(isoDate: string): Date | null {
  if (!ISO_DATE_PATTERN.test(isoDate)) return null;
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().startsWith(isoDate) ? date : null;
}

// The field only accepts "dd-mm-yyyy" (enforced by its input mask). We route through
// formatDateOfBirth rather than `new Date(dateOfBirth)` directly: for numeric dashed strings that
// assumes US MM-DD-YYYY ordering (e.g. `new Date('06-08-2000')` parses as 8 June, not 6 August).
function isValidDateOfBirth(dateOfBirth: string): boolean {
  if (!DATE_OF_BIRTH_PATTERN.test(dateOfBirth)) return false;
  return parseIsoDate(formatDateOfBirth(dateOfBirth)) !== null;
}

// Expects an ISO "yyyy-mm-dd" date — the shape the API and the OIDC `birthdate` claim both use. A
// longer ISO string (e.g. a full datetime) is accepted and truncated to its date part. Form values
// are "dd-mm-yyyy" and must go through formatDateOfBirth first. Returns NaN for anything that isn't
// a real calendar date, so callers can tell "no usable birth date" apart from a real age.
export function calculateAge(isoBirthDate: string | undefined | null) {
  // typeof rather than a truthiness check: `birthdate` is typed as a string but reaches us from a
  // JWT claim, and a non-string would throw on slice — inside a computed that renders during SSR.
  const birth = typeof isoBirthDate === 'string' ? parseIsoDate(isoBirthDate.slice(0, 10)) : null;
  if (!birth) {
    return NaN;
  }

  // `new Date('yyyy-mm-dd')` is UTC midnight, so read the birth date back with UTC getters —
  // local getters would shift it a day in timezones behind UTC.
  const today = new Date();
  let age = today.getFullYear() - birth.getUTCFullYear();
  const m = today.getMonth() - birth.getUTCMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getUTCDate())) {
    age--;
  }
  return age;
}

const dateOfBirthSchemaType = (enabled: boolean, checkAge = true) => {
  const schema = yup
    .string()
    .required('form.dateOfBirth.required')
    .test('is-valid-date', 'form.dateOfBirth.invalid', isValidDateOfBirth)
    .test('is-valid-age', 'form.dateOfBirth.age', (dateOfBirth: string) => {
      if (!enabled || !checkAge) {
        return true;
      }
      const age = calculateAge(formatDateOfBirth(dateOfBirth));
      return age >= 18;
    });

  return enabled ? schema : yup.string().optional();
};

export { dateOfBirthSchemaType };
